import breadcrumbs from 'assets/img/breadcrumbs.svg';
import Button from 'components/Button/Button';
import Text from 'components/Text/Text';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import { TFileId, TLanguageIDs, TMediaFileType, IMaxFileSizeMBStyleSettings } from 'types';
import { postMediaFileForStyleSettings } from 'backend/additionalInformation';
import { useFetchData } from 'hooks/useFetchData';
import { downloadBlob } from 'backend/additionalInformation';
import { AxiosResponse } from 'axios';
import {
	StyleSettingsImageUploaderContext,
	IStyleSettingsImageUploaderContext,
} from '../styleSettingsImageUploaderState/styleSettingsImageUploaderContext';
import ModalLayout from 'components/ModalLayout';
import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { openBrowseFile } from 'utils/openBrowseFile';
import style from './CropperModal.module.scss';

type TExtensionOfFile = 'gif' | 'image';
type TOutputMime = 'image/png' | 'image/jpeg';

interface IProps {
	filePath: string | null;
	imageOrientation?: 'horizontal' | 'vertical';
	horizontalAspectRatio?: number;
	verticalAspectRatio?: number;
	maxFileSizeMB?: IMaxFileSizeMBStyleSettings;
	translationId: string;
	languageId: TLanguageIDs;
	groupId: string;
	mediaFileType: TMediaFileType;
	hasAbilityToUploadGif?: boolean;
	showGroupLoader: () => void;
	hideGroupLoader: () => void;
	setMediaSettingsFromNormalDisplay: (
		languageId: null | TLanguageIDs,
		groupId: string,
		translationId: string,
		mediaFileType: TMediaFileType,
		sourceUrl: string,
		fileId: TFileId,
	) => void;
}

export const CropperModal: React.FC<IProps> = ({
	filePath,
	imageOrientation,
	horizontalAspectRatio,
	verticalAspectRatio,
	maxFileSizeMB,
	translationId,
	languageId,
	groupId,
	mediaFileType,
	hasAbilityToUploadGif = false,
	showGroupLoader,
	hideGroupLoader,
	setMediaSettingsFromNormalDisplay,
}) => {
	const {
		changeTypeOfModalWindow,
		changeTypeOfErrorModalWindow,
		setUploaderSrc,
		setCropperSrc,
		uploadImage,
		setNewImageUploaded,
		cropperSrc,
		typeOfModalWindow,
		uploaderSrc,
	} = useContext<IStyleSettingsImageUploaderContext>(StyleSettingsImageUploaderContext);
	const [crop, setCrop] = useState<Crop | undefined>(undefined);
	const [isCropAreaChanged, setCropAreaChanged] = useState<boolean>(false);
	const [isSentOriginalImg, setSentOriginalImg] = useState<boolean>(true);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const imageRef = useRef<HTMLImageElement | null>(null);
	const [loadingImageErrorInModal, setLoadingImageErrorInModal] = useState<boolean>(false);
	const [isFirstOpen, setIsFirstOpen] = useState<boolean>(true);
	const [gifFile, setGifFile] = useState<File | Blob | null>(null);
	const [imageFile, setImageFile] = useState<File | Blob | null>(null);
	const [extensionOfFile, setExtensionOfFile] = useState<TExtensionOfFile>();
	const [allowFileUpload, setAllowFileUpload] = useState<boolean>(true);
	const [outputMime, setOutputMime] = useState<TOutputMime>('image/jpeg');

	const inferMimeFromPath = (path: string): TOutputMime => {
		if (path.endsWith('.png')) {
			return 'image/png';
		}
		if (path.endsWith('.jpg') || path.endsWith('.jpeg')) {
			return 'image/jpeg';
		}
		return 'image/jpeg';
	};

	// TODO think about it later.
	const [dataToUpload, setDataToUpload] = useState<{
		status: 'success' | 'error';
		sourceUrl?: string | null;
		fileId?: string | null;
	}>();

	useEffect(() => {
		if (dataToUpload?.status === 'success' && dataToUpload.sourceUrl && dataToUpload.fileId) {
			setMediaSettingsFromNormalDisplay(
				languageId,
				groupId,
				translationId,
				mediaFileType,
				dataToUpload.sourceUrl,
				dataToUpload.fileId,
			);
			hideGroupLoader();
			setUploaderSrc(dataToUpload.sourceUrl);
		} else if (dataToUpload?.status === 'error') {
			hideGroupLoader();
			changeTypeOfErrorModalWindow('fileUploadError');
		}
		setDataToUpload(undefined);
		setAllowFileUpload(true);
	}, [dataToUpload]);
	// TODO think about it later.

	const aspectRatio = imageOrientation === 'horizontal' ? horizontalAspectRatio : verticalAspectRatio;

	// Download image blob from s3, then set img src from localhost to avoid tainted canvas exception
	const { refetch, response, loading } = useFetchData<AxiosResponse<Blob>, string>(
		filePath && !isFirstOpen ? downloadBlob : null,
		filePath || '',
	);

	useEffect(() => {
		if (isFirstOpen && typeOfModalWindow === 'cropper' && uploaderSrc) {
			setIsFirstOpen(false);
			refetch();
		} else if (typeOfModalWindow === 'cropper') {
			setIsFirstOpen(false);
		}
	}, [typeOfModalWindow]);

	useEffect(() => {
		if (response) {
			const urlFromBlob = URL.createObjectURL(response.data);
			setCropperSrc(urlFromBlob);
			const typeOfFile = response.config.url?.split('.').pop() === 'gif' ? 'gif' : 'image';
			setExtensionOfFile(typeOfFile);
			if (typeOfFile === 'image') {
				setImageFile(response.data);
				setOutputMime(inferMimeFromPath(filePath || ''));
			} else if (typeOfFile === 'gif') {
				setGifFile(response.data);
			}
		}
	}, [response]);

	const closeModal = () => {
		changeTypeOfModalWindow(null);
		setCrop(undefined);
		setCropAreaChanged(false);
		setSentOriginalImg(true);
	};

	const handleGifChange = async (files: FileList | null) => {
		setLoadingImageErrorInModal(false);
		setCropAreaChanged(true);
		setCrop(undefined);
		const imgFile = files ? files[0] : '';
		if (!imgFile) {
			return;
		} else if (maxFileSizeMB && imgFile.size > 1000 * 1000 * maxFileSizeMB.gif) {
			changeTypeOfErrorModalWindow('gifFileSizeExceeded');
		} else if (imgFile.type === 'image/gif') {
			const sourceUrl = URL.createObjectURL(imgFile);
			setCropperSrc(sourceUrl);
			setImageFile(null);
			setGifFile(imgFile);
			setExtensionOfFile('gif');
		} else {
			changeTypeOfErrorModalWindow('invalidGifFormat');
		}
	};

	const handleImageChange = (files: FileList | null) => {
		setLoadingImageErrorInModal(false);
		setCropAreaChanged(true);
		setCrop(undefined);
		const imgFile = files ? files[0] : '';
		if (!imgFile) {
			return;
		} else if (maxFileSizeMB && imgFile.size > 1000 * 1000 * maxFileSizeMB.img) {
			changeTypeOfErrorModalWindow('imgFileSizeExceeded');
		} else if (imgFile.type === 'image/jpeg' || imgFile.type === 'image/png') {
			const sourceUrl = URL.createObjectURL(imgFile);
			setCropperSrc(sourceUrl);
			setImageFile(imgFile);
			setGifFile(null);
			setExtensionOfFile('image');
			setOutputMime(imgFile.type);
		} else {
			changeTypeOfErrorModalWindow('invalidImageFormat');
		}
	};

	const drawImageInCanvas = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
		if (canvasRef.current && imageRef.current) {
			canvasRef.current.width = imageRef.current.naturalWidth;
			canvasRef.current.height = imageRef.current.naturalHeight;

			const context = canvasRef.current.getContext('2d');
			context?.drawImage(e.currentTarget, 0, 0, canvasRef.current.width, canvasRef.current.height);
		}
	};

	const handleCropAreaChange = (c: PixelCrop) => {
		setCrop(c);
		isSentOriginalImg && setSentOriginalImg(false);
		if (canvasRef.current && imageRef.current) {
			setCropAreaChanged(true);
			canvasRef.current.width = c.width;
			canvasRef.current.height = c.height;
			const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
			const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

			const context = canvasRef.current.getContext('2d');
			context?.drawImage(
				imageRef.current,
				c.x * scaleX,
				c.y * scaleY,
				c.width * scaleX,
				c.height * scaleY,
				0,
				0,
				c.width,
				c.height,
			);
		}
	};

	// TODO think about it later.
	const applyGif = async () => {
		try {
			if (gifFile) {
				const formData = new FormData();
				formData.append('file', gifFile, 'image.gif');
				const {
					data: { fileId },
				} = await postMediaFileForStyleSettings(formData);
				const sourceUrl = URL.createObjectURL(gifFile);
				setDataToUpload({ status: 'success', sourceUrl, fileId });
			}
		} catch (err) {
			setDataToUpload({ status: 'error' });
		}
	};

	const handleApplyImageSize = () => {
		if (canvasRef.current?.width === 0 && canvasRef.current.height === 0 && imageFile) {
			(async () => {
				const sourceUrl = URL.createObjectURL(imageFile);
				const fileId = await uploadImage(imageFile);
				fileId
					? setDataToUpload({ status: 'success', sourceUrl, fileId })
					: setDataToUpload({ status: 'error' });
			})();
		} else if (isSentOriginalImg && imageFile) {
			(async () => {
				const sourceUrl = URL.createObjectURL(imageFile);
				const fileId = await uploadImage(imageFile);
				fileId
					? setDataToUpload({ status: 'success', sourceUrl, fileId })
					: setDataToUpload({ status: 'error' });
			})();
		} else {
			const quality = outputMime === 'image/jpeg' ? 0.8 : undefined;
			canvasRef.current?.toBlob(
				async (blob) => {
					if (blob) {
						const sourceUrl = URL.createObjectURL(blob);
						const fileId = (await uploadImage(blob)) || '';
						fileId
							? setDataToUpload({ status: 'success', sourceUrl, fileId })
							: setDataToUpload({ status: 'error' });
					}
				},
				outputMime,
				quality,
			);
		}
	};

	const handleApply = () => setAllowFileUpload(false);

	useEffect(() => {
		if (!allowFileUpload) {
			closeModal();
			showGroupLoader();
			if (extensionOfFile === 'image') {
				handleApplyImageSize();
			} else if (extensionOfFile === 'gif') {
				applyGif();
			}
			setLoadingImageErrorInModal(false);
			setNewImageUploaded(true);
		}
	}, [allowFileUpload]);
	// TODO think about it later.

	const showErrorImage = () => {
		setLoadingImageErrorInModal(true);
	};

	const handleUploadGif = () => {
		openBrowseFile({
			onChoose: handleGifChange,
			accept: '.gif',
		});
	};

	const handleUploadImage = () => {
		openBrowseFile({
			onChoose: handleImageChange,
			accept: '.jpg, .jpeg, .png',
		});
	};

	const renderImage = () => {
		if (loading) {
			return (
				<div className={style.loaderContainer}>
					<LoaderWithProps loaderSize="big" />;
				</div>
			);
		}

		if (!cropperSrc) {
			return (
				<div className={style.noImageContainer}>
					<svg className={style.noImageIcon}>
						<use xlinkHref={`${breadcrumbs}#twoHills`} />
					</svg>
				</div>
			);
		}

		if (loadingImageErrorInModal) {
			return (
				<div className={style.brokenImageContainer}>
					<svg className={style.brokenImageIcon}>
						<use xlinkHref={`${breadcrumbs}#brokenPicture`} />
					</svg>
					<div>Error image</div>
				</div>
			);
		}

		return (
			<ReactCrop
				className={style.imageContainer}
				crop={crop}
				aspect={aspectRatio}
				disabled={extensionOfFile === 'gif'}
				onChange={handleCropAreaChange}>
				<img
					ref={imageRef}
					src={cropperSrc}
					onClick={() => {
						extensionOfFile === 'gif' && setCropAreaChanged(true);
					}}
					onLoad={drawImageInCanvas}
					onError={showErrorImage}
					alt="Photo"
				/>
			</ReactCrop>
		);
	};

	return (
		<ModalLayout width="60%" isVisible={typeOfModalWindow === 'cropper'} setIsShowModal={closeModal}>
			<div className={style.content}>
				{renderImage()}
				{!loading && (
					<div className={style.buttons}>
						{hasAbilityToUploadGif && (
							<Button onClick={handleUploadGif} data-test="chooseGifButton">
								<Text translationNameSpace="form" translationKey="chooseGifButton" asFragment={true} />
							</Button>
						)}
						<Button onClick={handleUploadImage} data-test="chooseImageButton">
							<Text translationNameSpace="form" translationKey="chooseImageButton" asFragment={true} />
						</Button>
						<Button
							onClick={handleApply}
							disabled={!cropperSrc || !isCropAreaChanged || !allowFileUpload}
							data-test="applyButton">
							<Text translationNameSpace="form" translationKey="applyButton" asFragment={true} />
						</Button>
					</div>
				)}
			</div>
			<canvas className={style.canvas} ref={canvasRef} />
		</ModalLayout>
	);
};
